import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import Hls from 'hls.js'
import { Maximize2, MessageCircle, Play } from 'lucide-react'
import { MuscleService } from '@entities/muscle'
import { WorkoutMedia } from '@entities/exercise'
import { Content, DefaultPage } from '@shared/layout/DefaultPage'
import { COLORS } from '@assets/styles/colors'
import { NavigationArrow } from '@ui/NavigationArrow'
import { getMediaRoleUrl, getMediaUrl } from '@shared/helpers/getMediaUrl'
import { getVisibleVideoAspectRatio } from '@shared/lib/video/getVisibleVideoAspectRatio'
import { FilterMuscleCard } from '@pages/TrainingConstructor/ui/filter/muscleCard'
import {
	FILTER_MUSCLE_SECTIONS,
	FilterMuscleItem,
} from '@pages/TrainingConstructor/ui/filter/filterCatalog'
import ExerciseFallbackImage from '@images/ExerciseHistoryMock.png'
import TisitLogo from '@assets/icons/tisit_logo.png'

type ExerciseInfoTab = 'technique' | 'muscles'

const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9
const SQUARE_ASPECT_RATIO_TOLERANCE = 0.1

export type ExerciseDetailsData = {
	name: string
	description: string
	technique?: string
	muscle_group_codes: string[]
	media: WorkoutMedia
}

interface ExerciseDetailsViewProps {
	exercise: ExerciseDetailsData
}

interface ExerciseDetailsScreenProps {
	exercise: ExerciseDetailsData
	onBack: () => void
	onAskCoach?: () => void
}

const MUSCLE_ITEMS_BY_CODE = FILTER_MUSCLE_SECTIONS.flatMap(
	(section) => section.items,
).reduce<Record<string, FilterMuscleItem>>((acc, item) => {
	acc[item.code] = item
	return acc
}, {})

const getExercisePosterUrl = (media: WorkoutMedia): string | undefined =>
	getMediaRoleUrl(media, ['cover', 'icon', 'muscle_groups'])

const getExerciseVideoUrl = (media: WorkoutMedia): string | undefined =>
	getMediaUrl(media.video)

const getSafeMuscleTitle = (code: string): string => {
	try {
		return MuscleService.getNameFromCode(code, 'ru')
	} catch {
		return code
	}
}

const getExerciseMuscles = (exercise: ExerciseDetailsData): FilterMuscleItem[] => {
	const items = exercise.muscle_group_codes.map((code) => {
		const catalogItem = MUSCLE_ITEMS_BY_CODE[code]
		if (catalogItem) {
			return catalogItem
		}

		return null
	})

	return items.filter((item): item is FilterMuscleItem => item !== null)
}

const splitExerciseText = (text: string): string[] => {
	const normalized = text.trim()
	if (!normalized) {
		return []
	}

	if (normalized.includes('\n')) {
		return normalized.split(/\n+/).map((line) => line.trim()).filter(Boolean)
	}

	return normalized
		.split(/(?<=[.!?])\s+/)
		.map((line) => line.trim())
		.filter(Boolean)
}

export const ExerciseInfoVideo: FC<{
	videoUrl?: string
	posterUrl?: string
	title: string
}> = ({ videoUrl, posterUrl, title }) => {
	const videoRef = useRef<HTMLVideoElement>(null)
	const videoCardRef = useRef<HTMLDivElement>(null)
	const hlsRef = useRef<Hls | null>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const [aspectRatio, setAspectRatio] = useState<number | null>(null)
	const [visibleAspectRatio, setVisibleAspectRatio] = useState<number | null>(
		null,
	)
	const hasVideoLayout = aspectRatio !== null || visibleAspectRatio !== null
	const isLandscape = aspectRatio !== null && aspectRatio > 1
	const isSquareish =
		visibleAspectRatio === null &&
		aspectRatio !== null &&
		!isLandscape &&
		Math.abs(aspectRatio - 1) <= SQUARE_ASPECT_RATIO_TOLERANCE
	const isVertical =
		visibleAspectRatio === null &&
		!isSquareish &&
		aspectRatio !== null &&
		aspectRatio < 1
	const displayAspectRatio =
		visibleAspectRatio ??
		(isLandscape
			? aspectRatio
			: isSquareish
				? DEFAULT_VIDEO_ASPECT_RATIO
				: isVertical
					? 1
					: (aspectRatio ?? DEFAULT_VIDEO_ASPECT_RATIO))
	const objectFit = isVertical ? 'contain' : 'cover'
	const videoCardClassName = [
		'exercise-info-video-card',
		isLandscape ? 'is-landscape' : '',
		isVertical ? 'is-vertical' : '',
		visibleAspectRatio ? 'has-visible-crop' : '',
		hasVideoLayout ? '' : 'is-layout-pending',
	]
		.filter(Boolean)
		.join(' ')

	useEffect(() => {
		const video = videoRef.current
		if (!videoUrl || !video) {
			return
		}

		setAspectRatio(null)
		setVisibleAspectRatio(null)
		setIsPlaying(false)

		if (hlsRef.current) {
			hlsRef.current.destroy()
			hlsRef.current = null
		}

		if (video.canPlayType('application/vnd.apple.mpegurl')) {
			video.src = videoUrl
		} else if (Hls.isSupported()) {
			const hls = new Hls()
			hls.loadSource(videoUrl)
			hls.attachMedia(video)
			hlsRef.current = hls
		} else {
			video.src = videoUrl
		}

		return () => {
			if (hlsRef.current) {
				hlsRef.current.destroy()
				hlsRef.current = null
			}
		}
	}, [videoUrl])

	const updateAspectRatio = () => {
		const video = videoRef.current
		if (video?.videoWidth && video.videoHeight) {
			setAspectRatio(video.videoWidth / video.videoHeight)
		}
	}

	const updateVisibleAspectRatio = () => {
		const video = videoRef.current
		if (!video) {
			return
		}

		const nextVisibleAspectRatio = getVisibleVideoAspectRatio(video)
		if (nextVisibleAspectRatio) {
			setVisibleAspectRatio(nextVisibleAspectRatio)
		}
	}

	const updateVideoLayout = () => {
		updateAspectRatio()
		updateVisibleAspectRatio()
	}

	const togglePlayback = async () => {
		const video = videoRef.current
		if (!video || !videoUrl) {
			return
		}

		if (video.paused) {
			await video.play()
			updateVisibleAspectRatio()
			setIsPlaying(true)
			return
		}

		video.pause()
		setIsPlaying(false)
	}

	const pauseVideo = () => {
		const video = videoRef.current
		if (video && !video.paused) {
			video.pause()
		}
	}

	const openFullscreen = async () => {
		const video = videoRef.current
		if (!video) {
			return
		}

		const fullscreenVideo = video as HTMLVideoElement & {
			webkitEnterFullscreen?: () => void
		}

		if (fullscreenVideo.webkitEnterFullscreen) {
			fullscreenVideo.webkitEnterFullscreen()
			return
		}

		if (video.requestFullscreen) {
			await video.requestFullscreen()
			return
		}

		await videoCardRef.current?.requestFullscreen?.()
	}

	if (!videoUrl) {
		return (
			<div className="exercise-info-video-card">
				<img
					className="exercise-info-video-fallback"
					src={posterUrl || ExerciseFallbackImage}
					alt={title}
				/>
			</div>
		)
	}

	return (
		<div
			ref={videoCardRef}
			className={videoCardClassName}
			style={{
				aspectRatio: displayAspectRatio,
				backgroundColor: '#000',
				overflow: 'hidden',
				position: 'relative',
				width: '100%',
			}}
		>
			<video
				ref={videoRef}
				className="exercise-info-video"
				style={{
					backgroundColor: '#000',
					display: 'block',
					height: '100%',
					objectFit,
					width: '100%',
				}}
				poster={posterUrl}
				loop
				playsInline
				preload="auto"
				onLoadedMetadata={updateAspectRatio}
				onLoadedData={updateVideoLayout}
				onCanPlay={updateVideoLayout}
				onResize={updateVideoLayout}
				onPlay={() => setIsPlaying(true)}
				onPause={() => setIsPlaying(false)}
				onEnded={() => setIsPlaying(false)}
				onClick={pauseVideo}
				aria-label={`Видео упражнения ${title}`}
			/>
			{!isPlaying && (
				<button
					type="button"
					className="exercise-info-video-toggle"
					onClick={togglePlayback}
					aria-label="Воспроизвести видео"
				>
					<Play size={24} strokeWidth={2.4} aria-hidden="true" />
				</button>
			)}
			{isPlaying && (
				<button
					type="button"
					className="exercise-info-video-fullscreen"
					onClick={openFullscreen}
					aria-label="Открыть видео на весь экран"
				>
					<Maximize2 size={20} strokeWidth={2.2} aria-hidden="true" />
				</button>
			)}
		</div>
	)
}

const ExerciseInfoTabs: FC<{
	activeTab: ExerciseInfoTab
	onChange: (tab: ExerciseInfoTab) => void
}> = ({ activeTab, onChange }) => (
	<div className="exercise-info-tabs" role="tablist" aria-label="Информация об упражнении">
		<button
			type="button"
			role="tab"
			aria-selected={activeTab === 'technique'}
			className={activeTab === 'technique' ? 'is-active' : ''}
			onClick={() => onChange('technique')}
		>
			Техника
		</button>
		<button
			type="button"
			role="tab"
			aria-selected={activeTab === 'muscles'}
			className={activeTab === 'muscles' ? 'is-active' : ''}
			onClick={() => onChange('muscles')}
		>
			Мышцы
		</button>
	</div>
)

const TechniquePanel: FC<{ exercise: ExerciseDetailsData }> = ({ exercise }) => {
	const textBlocks = splitExerciseText(exercise.technique || exercise.description)

	return (
		<section className="exercise-info-panel exercise-info-technique-panel" role="tabpanel">
			<h2>Как выполнять</h2>
			{textBlocks.length > 0 ? (
				<ol className="exercise-info-technique-list">
					{textBlocks.map((text) => (
						<li key={text}>{text}</li>
					))}
				</ol>
			) : (
				<p>Описание техники появится после обновления карточки упражнения.</p>
			)}
		</section>
	)
}

const MusclesPanel: FC<{ exercise: ExerciseDetailsData }> = ({ exercise }) => {
	const muscleItems = useMemo(() => getExerciseMuscles(exercise), [exercise])
	const muscleGroupsUrl = getMediaUrl(exercise.media.muscle_groups)
	const muscleStripClassName =
		muscleItems.length > 0 && muscleItems.length < 4
			? 'exercise-info-muscle-strip is-centered'
			: 'exercise-info-muscle-strip'

	return (
		<section className="exercise-info-panel exercise-info-muscles-panel" role="tabpanel">
			<h2>Область внимания</h2>
			{muscleItems.length > 0 ? (
				<div className={muscleStripClassName} aria-label="Задействованные мышцы">
					{muscleItems.map((muscle) => (
						<FilterMuscleCard
							key={muscle.code}
							muscle={muscle}
							isReadonly
							className="exercise-info-muscle-card"
						/>
					))}
				</div>
			) : (
				<div className="exercise-info-muscle-fallback">
					{exercise.muscle_group_codes.map((code) => (
						<span key={code}>{getSafeMuscleTitle(code)}</span>
					))}
				</div>
			)}
			{muscleGroupsUrl && (
				<div className="exercise-info-body-map">
					<img src={muscleGroupsUrl} alt="Анатомическая схема задействованных мышц" />
				</div>
			)}
		</section>
	)
}

export const ExerciseDetailsView: FC<ExerciseDetailsViewProps> = ({ exercise }) => {
	const [activeTab, setActiveTab] = useState<ExerciseInfoTab>('technique')
	const videoUrl = getExerciseVideoUrl(exercise.media)
	const posterUrl = getExercisePosterUrl(exercise.media)

	return (
		<>
			<ExerciseInfoVideo
				videoUrl={videoUrl}
				posterUrl={posterUrl}
				title={exercise.name}
			/>
			<ExerciseInfoTabs activeTab={activeTab} onChange={setActiveTab} />
			{activeTab === 'technique' ? (
				<TechniquePanel exercise={exercise} />
			) : (
				<MusclesPanel exercise={exercise} />
			)}
		</>
	)
}

export const ExerciseDetailsScreen: FC<ExerciseDetailsScreenProps> = ({
	exercise,
	onBack,
	onAskCoach,
}) => (
	<DefaultPage
		style={{ background: COLORS.DEFAULT_BACKGROUND }}
		className="exercise-info-page"
	>
		<Content className="exercise-info-content" style={{ '--background': COLORS.DEFAULT_BACKGROUND }}>
			<header className="exercise-info-header">
				<NavigationArrow
					direction="left"
					onClick={onBack}
					className="exercise-info-back"
				/>
				<h1>{exercise.name}</h1>
				<img className="exercise-info-logo" src={TisitLogo} alt="TISIT" />
			</header>
			{onAskCoach ? (
				<button type="button" className="exercise-info-coach-button" onClick={onAskCoach}>
					<MessageCircle size={17} strokeWidth={2.25} />
					Спросить тренера
				</button>
			) : null}
			<ExerciseDetailsView exercise={exercise} />
		</Content>
	</DefaultPage>
)
